/** 匿名屋台評価のpt受取処理を画面間で共有する。 */
import { useCallback, useRef, useState } from 'react';
import useNetworkStatus from '../../../shared/hooks/useNetworkStatus';
import { logError } from '../../../shared/utils/logger';
import { VISITOR_OFFLINE_SUBMIT_BLOCKED_MESSAGE } from '../constants';
import {
  visitorClaimStallRatingReward,
  VISITOR_STALL_RATING_SESSION_INVALID_CODE,
} from '../services/visitorStallRatingService';

/** pt受取APIの確定結果。 */
type RewardClaimResult = Awaited<ReturnType<typeof visitorClaimStallRatingReward>>;

/**
 * QRや屋台選択を必要とせず、ログイン済み来場者のpt受取コードを交換する。
 * @param options - セッションと成功・失効時の画面処理
 * @returns 受取操作と表示状態
 */
const useStallRatingRewardClaim = ({ sessionToken, onSessionLost, onSuccess }: {
  sessionToken: string;
  onSessionLost: () => Promise<void>;
  onSuccess: (result: RewardClaimResult) => void;
}) => {
  /** 通信可否。 */
  const { isOnline } = useNetworkStatus();
  /** 再描画前の連打も防ぐ送信ロック。 */
  const isClaimingRef = useRef(false);
  /** 受取処理中の表示。 */
  const [isClaimingReward, setIsClaimingReward] = useState(false);
  /** サーバー確定後の受取結果。 */
  const [rewardClaimResult, setRewardClaimResult] = useState<RewardClaimResult | null>(null);
  /** 受取失敗の案内。 */
  const [rewardClaimError, setRewardClaimError] = useState('');

  /**
   * 入力コードを既存の受取APIへ送信する。
   * @param rewardCode - 匿名回答後に保存した12文字コード
   * @returns 受取処理の完了
   */
  const handleClaimRewardCode = useCallback(async (rewardCode: string) => {
    if (!sessionToken || isClaimingRef.current) return;
    setRewardClaimError('');
    setRewardClaimResult(null);
    if (!isOnline) {
      setRewardClaimError(VISITOR_OFFLINE_SUBMIT_BLOCKED_MESSAGE);
      return;
    }
    isClaimingRef.current = true;
    setIsClaimingReward(true);
    try {
      /** サーバーが確定した付与ptと残高。 */
      const result = await visitorClaimStallRatingReward(sessionToken, rewardCode);
      setRewardClaimResult(result);
      onSuccess(result);
    } catch (error) {
      // 受取コードやセッションをログへ含めない。
      logError('匿名屋台評価ptコードの交換に失敗しました。', { errorCode: error?.code });
      if (error?.code === VISITOR_STALL_RATING_SESSION_INVALID_CODE) {
        await onSessionLost();
        return;
      }
      setRewardClaimError(
        error instanceof Error ? error.message : 'pt受取コードの交換に失敗しました。',
      );
    } finally {
      isClaimingRef.current = false;
      setIsClaimingReward(false);
    }
  }, [isOnline, onSessionLost, onSuccess, sessionToken]);

  return { handleClaimRewardCode, isClaimingReward, rewardClaimError, rewardClaimResult,
    isClaimDisabled: !isOnline || !sessionToken };
};

export default useStallRatingRewardClaim;
